import { useEffect, useMemo, useRef, useState } from 'react';
import CustomToolTip from '../../../../../../CommonComponents/CustomToolTip';
import { Command, CustomScheduleModalFormType, RecentlyEditedTelecommandType, ScheduleData, ScheduleStatusType, ScheduleTask } from '../../../../../types';
import { TaskNotPerformedSvg, TaskUpcommingSvg, SpinLoaderSvg, PayloadTaskSvg, SubSystemSvg, TaskCompleteSvg } from '../../../modals/Svgs';
import { ChevronDownSvg, AddNewCommandIcon, TeleCommandsIcon, NoCommandSelectedIcon, NoSchedulePreview } from '../../Svgs';
import EmptyPage from '../../../../../custom_components/EmptyPage';
import NewAddButton from './NewAddButton';
import GroupButtonList from '../../../../../../CommonComponents/GroupButtonList';
import { get_task_status_icon, getContactCurrentStatus, getTaskTypeDisplayLabel, MenuList } from '../../../../../utils';
import { base64ToHex } from '../../../../../../../utils/commonFunctions';
import { scrollAndBlinkEditedRow } from './scheduleRowUtils';
import useScenario from '../../../../../hooks/useScenario';
import useSocket from '../../../../../hooks/useSocket';
import useContacts from '../../../../../hooks/useContacts';
import { ADCSSvg, PowerSvg, DataFlowSvg } from '../../../../../../UnifiedControlPlane/assets/icons/Icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

type TabType = 'telecommand' | 'raw-data';

const TabOptions = [
    { label: 'Telecommand', value: 'telecommand' },
    { label: 'Raw Data', value: 'raw-data' },
];

interface ScheduleTaskViewProps {
    showAddButton?: CustomScheduleModalFormType;
    readonly: boolean;
    currentTime?: number;
    selectedSchedule?: ScheduleData | null;
    allTasks: ScheduleTask[];
    selectedTask: ScheduleTask | null;
    selectedTelecommand: Command | null;
    recentlyEditedTelecommand?: RecentlyEditedTelecommandType | null;
    onMenuClick: (menu: string, view: 'readonly' | 'editable', type: string, data: { name: string; scheduleId: string; taskId?: string; commandId?: string }) => void;
    onTaskSelect: (view: 'readonly' | 'editable', task: ScheduleTask) => void;
    onCommandSelect: (view: 'readonly' | 'editable', command: Command) => void;
    onAddTask?: (task: ScheduleTask) => void;
    onAddCommand?: (task: ScheduleTask, command: Command | null) => void;
    scheduleType?: string;
}

const getComponentIcon = (name: string) => {
    const s = (name || '').toLowerCase();
    if (s.includes('adcs')) return <ADCSSvg color="#F0F0F0" />;
    if (s.includes('eps') || s.includes('ps') || s.includes('power') || s.includes('battery')) return <PowerSvg color="#F0F0F0" />;
    if (s.includes('obc') || s.includes('obs') || s.includes('nvidia') || s.includes('cpu')) return <PayloadTaskSvg />;
    if (s.includes('sband') || s.includes('s-band') || s.includes('xband') || s.includes('x-band') || s.includes('comms')) return <DataFlowSvg color="#F0F0F0" />;
    return <SubSystemSvg />;
};

const formatTaskDuration = (duration?: number) => {
    if (typeof duration !== 'number' || duration <= 0) return '0 min';
    if (duration >= 60) {
        return `${Math.round(duration / 60)} min`;
    }
    return `${duration} sec`;
};

const resolveTaskIdForEntries = (entries: any[], tasks: ScheduleTask[]): string | null => {
    if (!Array.isArray(entries) || entries.length === 0 || !Array.isArray(tasks) || tasks.length === 0) {
        return null;
    }

    for (const entry of entries) {
        const entryId = entry.commandId != null ? String(entry.commandId) : null;
        const entryName = entry.commandName ? String(entry.commandName).trim().toLowerCase() : null;
        const entryEpochSec = entry.scheduledEpochMs ? Math.floor(entry.scheduledEpochMs / 1000) : null;

        const matchedTask = tasks.find((t) =>
            (t.commandsList || []).some((cmd) => {
                const cmdId = cmd.id != null ? String(cmd.id) : null;
                const cmdUuid = cmd.commandId != null ? String(cmd.commandId) : null;
                const cmdName = cmd.name ? String(cmd.name).trim().toLowerCase() : null;
                const cmdEpoch = cmd.epoch ?? null;

                if (entryId && (cmdId === entryId || cmdUuid === entryId)) {
                    return true;
                }
                if (entryEpochSec && cmdEpoch && entryEpochSec === cmdEpoch && entryName && cmdName === entryName) {
                    return true;
                }
                return false;
            })
        );

        if (matchedTask) {
            return matchedTask.taskId;
        }
    }

    return null;
};

export const findMatchingEntry = (command: Command, cmdIndex: number, entries: any[]): any => {
    if (!Array.isArray(entries) || entries.length === 0) return null;

    const cmdId = command.id != null ? String(command.id) : null;
    const cmdUuid = command.commandId != null ? String(command.commandId) : null;
    const cmdName = command.name ? String(command.name).trim().toLowerCase() : null;
    const cmdEpoch = command.epoch ?? (command.epochISO8601InMs ? Math.floor(new Date(command.epochISO8601InMs).getTime() / 1000) : null);

    // 1. Exact positional index match (1-to-1 array mapping for test case telecommands sequence)
    const byExactIndex = entries.find((e) => e.index === cmdIndex) ?? entries[cmdIndex];
    if (byExactIndex) {
        return byExactIndex;
    }

    // 2. Exact scheduledEpochMs timestamp match (unique per execution instance)
    if (cmdEpoch) {
        const byEpoch = entries.find((e) => e.scheduledEpochMs && Math.floor(e.scheduledEpochMs / 1000) === cmdEpoch);
        if (byEpoch) {
            return byEpoch;
        }
    }

    // 3. ID and Name match
    const byIdAndName = entries.find((e) => {
        const entryId = e.commandId != null ? String(e.commandId) : null;
        const entryName = e.commandName ? String(e.commandName).trim().toLowerCase() : null;
        const idMatches = Boolean(entryId && (cmdId === entryId || cmdUuid === entryId));
        const nameMatches = Boolean(entryName && cmdName && entryName === cmdName);
        return idMatches && nameMatches;
    });
    if (byIdAndName) return byIdAndName;

    // 4. ID match
    const byId = entries.find((e) => {
        const entryId = e.commandId != null ? String(e.commandId) : null;
        return Boolean(entryId && (cmdId === entryId || cmdUuid === entryId));
    });
    if (byId) return byId;

    // 5. Fallback by Name
    const byName = entries.find((e) => {
        const entryName = e.commandName ? String(e.commandName).trim().toLowerCase() : null;
        return Boolean(entryName && cmdName && entryName === cmdName);
    });
    return byName || null;
};

const ScheduleTaskView = ({
    showAddButton,
    readonly,
    currentTime,
    selectedSchedule,
    allTasks,
    selectedTask,
    selectedTelecommand,
    recentlyEditedTelecommand,
    onMenuClick,
    onTaskSelect,
    onCommandSelect,
    onAddTask,
    onAddCommand,
    scheduleType
}: ScheduleTaskViewProps) => {
    const [expanded, setExpanded] = useState(true);
    const [tab, setTab] = useState<TabType>('telecommand');
    const [openMenu, setOpenMenu] = useState<string | boolean>(false);
    const recentlyEditedRowRef = useRef<HTMLDivElement | null>(null);

    const selectedTaskCommands = useMemo(() => selectedTask?.commandsList || [], [selectedTask]);
    const disabledCommandSection = useMemo(() => !selectedTask, [selectedTask]);
    const filterTasks = useMemo(() => Array.from(new Map(allTasks.map((item) => [item.taskId, item])).values()), [allTasks]);

    const { scenario } = useScenario();

    const isValidateMode =
        (scenario?.scenarioDeploymentType ?? '').toUpperCase() === 'VALIDATE' ||
        selectedSchedule?.scheduleType === 'VALIDATION' ||
        (selectedSchedule as any)?.executionLocation === 'ONBOARD' ||
        selectedSchedule?.source === 'VALIDATION' ||
        (selectedSchedule?.scheduleType ?? '').toLowerCase() === 'validationrun';

    const { socket } = useSocket();
    const { get_all_onboard_schedules } = useContacts();
    const [taskTimelineMap, setTaskTimelineMap] = useState<Record<string, any[]>>({});
    const [testCaseLiveStatusMap, setTestCaseLiveStatusMap] = useState<Record<string, string>>({});
    const resultIdToTaskIdRef = useRef<Record<string, string>>({});

    useEffect(() => {
        if (!isValidateMode || !socket) return;

        const handleWsMessage = (event: MessageEvent) => {
            try {
                const parsed = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                const eventType = parsed?.event || parsed?.type || parsed?.eventType || parsed?.message;
                const body = parsed?.data || parsed?.body || parsed;

                if (body?.testCaseResultId && body?.testCaseId) {
                    resultIdToTaskIdRef.current[String(body.testCaseResultId)] = String(body.testCaseId);
                }
                if (body?.testCaseResultId && body?.taskId) {
                    resultIdToTaskIdRef.current[String(body.testCaseResultId)] = String(body.taskId);
                }

                // 1. Live status for telecommands and test case from TESTCASE_ONBOARD_TIMELINE_UPDATED
                if (eventType === 'TESTCASE_ONBOARD_TIMELINE_UPDATED') {
                    const entries = body?.entries;
                    if (Array.isArray(entries)) {
                        const targetTaskId =
                            body?.taskId ||
                            body?.testCaseId ||
                            (body?.testCaseResultId ? resultIdToTaskIdRef.current[String(body.testCaseResultId)] : null) ||
                            resolveTaskIdForEntries(entries, allTasks) ||
                            selectedTask?.taskId;

                        if (targetTaskId) {
                            setTaskTimelineMap((prev) => ({
                                ...prev,
                                [String(targetTaskId)]: entries,
                            }));
                        }
                    }

                    const tcKey = body?.testCaseResultId || body?.testCaseName;
                    if (tcKey) {
                        if (body?.runningEntries > 0) {
                            // Keep status as RUNNING while execution is in progress to avoid intermediate glitching
                            setTestCaseLiveStatusMap((prev) => ({
                                ...prev,
                                [String(tcKey)]: 'RUNNING',
                                ...(body?.testCaseName ? { [String(body.testCaseName).toLowerCase()]: 'RUNNING' } : {})
                            }));
                        } else if (body?.final === true || (body?.totalEntries > 0 && body?.completedEntries === body?.totalEntries)) {
                            // Timeline complete - refresh onboard schedules from API
                            get_all_onboard_schedules();
                        }
                    }
                }

                // 2. Test case completion / status WS events
                if (
                    eventType === 'TEST_CASE_EXECUTION_COMPLETE' ||
                    eventType === 'TEST_PLAN_EXECUTION_COMPLETE' ||
                    eventType === 'TESTCASE_RESULT_UPDATED' ||
                    eventType === 'TEST_CASE_EXECUTION_CANCELLED' ||
                    eventType === 'TEST_CASE_EXECUTION_ABORTED' ||
                    eventType === 'VALIDATION_TEST_CASE_ABORTED'
                ) {
                    const defaultFallback = (eventType.includes('CANCELLED') || eventType.includes('ABORTED')) ? 'FAILED' : 'COMPLETED';
                    const finalStatus = String(body?.status || defaultFallback).toUpperCase();

                    setTestCaseLiveStatusMap((prev) => {
                        const next = { ...prev };
                        if (body?.testCaseId) next[String(body.testCaseId)] = finalStatus;
                        if (body?.taskId) next[String(body.taskId)] = finalStatus;
                        if (body?.testCaseResultId) next[String(body.testCaseResultId)] = finalStatus;
                        if (body?.testCaseName) next[String(body.testCaseName).toLowerCase()] = finalStatus;
                        if (body?.taskName) next[String(body.taskName).toLowerCase()] = finalStatus;
                        if (body?.name) next[String(body.name).toLowerCase()] = finalStatus;
                        return next;
                    });

                    // Trigger get_all_onboard_schedules to sync with API single source of truth
                    get_all_onboard_schedules();
                }

                if (
                    eventType === 'TEST_CASE_EXECUTION_IN_PROGRESS' ||
                    eventType === 'TESTCASE_PROGRESS_UPDATED'
                ) {
                    setTestCaseLiveStatusMap((prev) => {
                        const next = { ...prev };
                        if (body?.testCaseId) next[String(body.testCaseId)] = 'RUNNING';
                        if (body?.taskId) next[String(body.taskId)] = 'RUNNING';
                        if (body?.testCaseResultId) next[String(body.testCaseResultId)] = 'RUNNING';
                        if (body?.testCaseName) next[String(body.testCaseName).toLowerCase()] = 'RUNNING';
                        if (body?.taskName) next[String(body.taskName).toLowerCase()] = 'RUNNING';
                        if (body?.name) next[String(body.name).toLowerCase()] = 'RUNNING';
                        return next;
                    });
                }
            } catch {
                // ignore
            }
        };

        socket.addEventListener('message', handleWsMessage);
        return () => {
            socket.removeEventListener('message', handleWsMessage);
        };
    }, [isValidateMode, socket, get_all_onboard_schedules]);

    // Auto-select first task if none is selected
    useEffect(() => {
        if (isValidateMode && readonly && !selectedTask && filterTasks.length > 0) {
            onTaskSelect('readonly', filterTasks[0]);
        }
    }, [isValidateMode, readonly, selectedTask, filterTasks, onTaskSelect]);

    const availableTabOptions = useMemo(() => {
        if (isValidateMode) {
            return TabOptions.filter((item) => item.value !== 'raw-data');
        }
        return TabOptions;
    }, [isValidateMode]);

    useEffect(() => {
        if (isValidateMode && tab === 'raw-data') {
            setTab('telecommand');
        }
    }, [isValidateMode, tab]);

    const hasCommands = useMemo(() => {
        return selectedTaskCommands?.length > 0;
    }, [selectedTaskCommands]);

    const displayTasks = useMemo(() => {
        return filterTasks;
    }, [filterTasks]);

    const ongoingSchedule = useMemo(() => selectedSchedule?.status === 'RUNNING', [selectedSchedule]);
    const executedSchedule = useMemo(() => selectedSchedule?.status === 'COMPLETED', [selectedSchedule]);
    const hideGroupButtons = useMemo(() => ongoingSchedule || executedSchedule, [ongoingSchedule, executedSchedule]);
    const scheduleId = useMemo(() => selectedSchedule?.scheduleId, [selectedSchedule]);
    const recentlyEditedCommandId = recentlyEditedTelecommand?.commandId;
    const recentlyEditedCommandKey = recentlyEditedTelecommand?.updatedAt;
    const showRecentlyEditedCommand =
        !!recentlyEditedCommandId && (!recentlyEditedTelecommand?.taskId || recentlyEditedTelecommand.taskId === selectedTask?.taskId);

    useEffect(() => {
        if (!showRecentlyEditedCommand) return;
        setTab('telecommand');
        setExpanded(true);
    }, [recentlyEditedCommandKey, showRecentlyEditedCommand]);

    useEffect(() => {
        if (!showRecentlyEditedCommand || tab !== 'telecommand' || !expanded) return;
        return scrollAndBlinkEditedRow(recentlyEditedRowRef.current);
    }, [expanded, recentlyEditedCommandKey, showRecentlyEditedCommand, tab]);

    const getDetails = (start: number, end: number) => {
        const baseDetails = {
            status: 'Upcoming',
            icon: <TaskUpcommingSvg />
        };
        if (!selectedSchedule || !selectedSchedule?.status || !currentTime) return baseDetails;
        const scheduleStatus = selectedSchedule?.status as ScheduleStatusType;
        const showDynamicDetails = ['RUNNING', 'COMPLETED'].includes(scheduleStatus);
        const expired = ['EXPIRED', 'ABOUT_TO_EXPIRE'].includes(scheduleStatus);
        if (['EXPIRED', 'ABORTED', 'ABOUT_TO_EXPIRE'].includes(scheduleStatus)) {
            return {
                status: expired ? 'Expired' : 'Aborted',
                icon: <TaskNotPerformedSvg />
            };
        } else if (showDynamicDetails && currentTime && readonly) {
            const status = getContactCurrentStatus(start, end, currentTime);
            const icon = get_task_status_icon(start, end, currentTime);
            return {
                status,
                icon
            };
        }
        return baseDetails;
    };

    const getTaskStatusInfo = (item: ScheduleTask) => {
        const liveStatus =
            testCaseLiveStatusMap[item.taskId] ||
            (item.taskName ? testCaseLiveStatusMap[item.taskName.toLowerCase()] : null) ||
            ((item as any)?.testCaseResultId && testCaseLiveStatusMap[(item as any).testCaseResultId]) ||
            item?.status;

        const rawStatus = String(liveStatus || '').toUpperCase();
        const parentStatus = String(selectedSchedule?.status || '').toUpperCase();

        // When schedule is completed in get_all_onboard_schedules API, testcase is Completed (Green checkmark)
        if (executedSchedule || parentStatus === 'COMPLETED' || ['COMPLETED', 'PASSED', 'SUCCESS', 'SUCCEEDED'].includes(rawStatus)) {
            return {
                icon: <TaskCompleteSvg />,
                title: 'Completed',
            };
        }

        const isRunning =
            ['RUNNING', 'IN_PROGRESS', 'EXECUTING'].includes(rawStatus) ||
            (parentStatus === 'RUNNING' && item.taskId === selectedTask?.taskId);

        if (isRunning) {
            return {
                icon: <SpinLoaderSvg className="rotating" />,
                title: 'Running',
            };
        }

        return {
            icon: <TaskUpcommingSvg />,
            title: 'Scheduled',
        };
    };

    return (
        <div className='right__section__body'>
            <div className='schedule__task__container'>
                <div className='schedule__table__container'>
                    <div className='schedule__table__container__header'>
                        {isValidateMode ? (
                            <div className='table__header__cell__container'>
                                <span className='table__cell header__cell w-4' />
                                <div className='table__cell header__cell w-35'>
                                    <span className='cell__detail'>TEST CASES</span>
                                </div>
                                <div className='table__cell header__cell w-35'>
                                    <span className='cell__detail'>COMPONENTS</span>
                                </div>
                                <div className='table__cell header__cell w-15'>
                                    <span className='cell__detail'>TOTAL TCs</span>
                                </div>
                                <div className='table__cell header__cell w-15'>
                                    <span className='cell__detail'>DURATION</span>
                                </div>
                            </div>
                        ) : (
                            <div className='table__header__cell__container'>
                                <span className='table__cell header__cell w-4' />
                                <div className='table__cell header__cell w-30'>
                                    <span className='cell__detail'>TASK</span>
                                </div>
                                <div className='table__cell header__cell w-30'>
                                    <span className='cell__detail'>TASK DETAILS</span>
                                </div>
                                <div className='table__cell header__cell w-25'>
                                    <span className='cell__detail'>START TIME</span>
                                </div>
                                <div className='table__cell header__cell w-10'>
                                    <span className='cell__detail'>DURATION</span>
                                </div>
                            </div>
                        )}
                    </div>
                    {(!displayTasks || displayTasks.length === 0) && (
                        <EmptyPage
                            classNames={readonly ? '' : 'cursor-pointer'}
                            icon={<NoSchedulePreview />}
                            content={isValidateMode ? 'No Test Cases Added Yet' : (scheduleType === 'Build' ? 'No Tasks Added Yet' : 'No Schedule to Preview')}
                            subContent={isValidateMode ? 'Execute an onboard validation run to preview test cases' : (scheduleType === 'Build' ? 'Add a task to start building your schedule' : 'Please generate a schedule to preview')}
                        />
                    )}
                    {displayTasks && displayTasks.length > 0 && (
                        <div className='schedule__table__container__body'>
                            {displayTasks.map((item) => {
                                const selected = item.taskId === selectedTask?.taskId;
                                const status = item?.status;
                                const disabled = !isValidateMode && status === 'Not Possible';
                                const message = item?.message || status || '';
                                const view = readonly ? 'readonly' : 'editable';
                                const showButton = showAddButton === 'add-task' && selected && !isValidateMode;
                                const details = getDetails(item?.startTime, item?.endTime);
                                const taskTypeLabel = getTaskTypeDisplayLabel(item?.type);

                                const statusInfo = getTaskStatusInfo(item);

                                const componentsList = Array.from(
                                    new Set(
                                        (item.commandsList || [])
                                            .map((cmd) => cmd.component || cmd.subsystem)
                                            .filter((c): c is string => Boolean(c))
                                    )
                                );
                                const finalComponents = componentsList.length > 0
                                    ? componentsList
                                    : Array.isArray((item as any)?.subsystems)
                                        ? (item as any).subsystems
                                        : [];

                                return (
                                    <div
                                        key={item.taskId}
                                        className={`table__body__cell__container
                                            ${selected ? 'active' : ''}
                                            ${disabled ? 'disabled' : ''}
                                            ${showButton ? 'show__button' : ''}
                                        `}
                                        onClick={() => {
                                            if (disabled) return;
                                            onTaskSelect(view, item);
                                        }}
                                    >
                                        {!readonly && !disabled && !isValidateMode && (
                                            <NewAddButton
                                                title='Add New Task'
                                                onClick={() => onAddTask?.(item)}
                                            />
                                        )}
                                        <div className='table__cell w-4'>
                                            <div className='cell__icon'>
                                                {isValidateMode ? (
                                                    <CustomToolTip title={statusInfo.title} placement='top'>
                                                        {statusInfo.icon}
                                                    </CustomToolTip>
                                                ) : (
                                                    (ongoingSchedule || executedSchedule || disabled) && (
                                                        <CustomToolTip title={disabled ? message : details?.status} placement='top'>
                                                            {disabled ? <TaskNotPerformedSvg /> : details?.icon}
                                                        </CustomToolTip>
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {isValidateMode ? (
                                            <>
                                                <div className='table__cell w-35'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={item?.taskName} placement='top'>
                                                            {item?.taskName}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-35'>
                                                    <div className='cell__detail'>
                                                        <div className='flex items-center gap-1.5 flex-wrap overflow-hidden py-1'>
                                                            {finalComponents.length > 0 ? (
                                                                finalComponents.map((comp: string, i: number) => (
                                                                    <div key={i} className='component__pill__badge'>
                                                                        <span className='pill__icon'>{getComponentIcon(comp)}</span>
                                                                        <span className='pill__label'>{comp}</span>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className='component__pill__badge'>
                                                                    <span className='pill__icon'><SubSystemSvg /></span>
                                                                    <span className='pill__label'>Subsystem</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-15'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={`${item?.commandsList?.length ?? 0} Commands`} placement='top'>
                                                            <span>{item?.commandsList?.length ?? 0}</span>
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-15'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={formatTaskDuration(item?.duration)} placement='top'>
                                                            <span>{formatTaskDuration(item?.duration)}</span>
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className='table__cell w-30'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={item?.taskName} placement='top'>
                                                            {item?.taskName}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-30'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={taskTypeLabel} placement='top'>
                                                            {taskTypeLabel}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-25'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={disabled ? '' : (item?.startTimeISO8601 || '--')} placement='top'>
                                                            {disabled ? '--' : (item?.startTimeISO8601 || '--')}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-10'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={disabled ? '--' : `${item?.duration ?? 0} sec`} placement='top'>
                                                            {disabled ? '--' : `${item?.duration ?? 0} sec`}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {!hideGroupButtons && !disabled && !isValidateMode && (
                                            <div className='menu__container'>
                                                <GroupButtonList
                                                    placement='left'
                                                    id={item?.taskId as string}
                                                    open={openMenu}
                                                    hiddenButtons={['Edit']}
                                                    toggle={(value) => {
                                                        if (disabled) return;
                                                        setOpenMenu(value);
                                                    }}
                                                    buttons={MenuList}
                                                    onClick={(btn) => {
                                                        onMenuClick(btn, view, 'task', {
                                                            name: item?.taskName,
                                                            scheduleId: scheduleId as string,
                                                            taskId: item?.taskId as string,
                                                        });
                                                    }}
                                                >
                                                    <i className='fe fe-more-vertical opacity-75' />
                                                </GroupButtonList>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div
                className={`schedule__telecommand__container 
                    ${disabledCommandSection ? 'disabled' : ''}
                    ${expanded && !disabledCommandSection ? 'active' : ''}
                `}
            >
                <div className='schedule__telecommand__container__header'>
                    {isValidateMode ? (
                        <div className='telecommand-section-title'>
                            <span>TELECOMMANDS</span>
                        </div>
                    ) : (
                        <div className='schedule__telecommand__container__header__button__container'>
                            {availableTabOptions.map((item) => (
                                <button
                                    key={item.value}
                                    className={`telecommand__container__header__button ${tab === item.value ? 'active' : ''}`}
                                    onClick={() => {
                                        setTab(item.value as TabType);
                                        setExpanded(true);
                                    }}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    )}
                    <CustomToolTip title={expanded ? 'Close' : 'Open'} placement='top'>
                        <button
                            className='schedule__telecommand__container__button'
                            onClick={() => setExpanded(!expanded)}
                        >
                            <ChevronDownSvg />
                        </button>
                    </CustomToolTip>
                </div>

                {!hasCommands && (
                    <EmptyPage
                        classNames={readonly ? '' : 'cursor-pointer'}
                        icon={readonly ? <NoCommandSelectedIcon /> : <AddNewCommandIcon />}
                        content={readonly ? 'No Telecommands' : 'No Telecommands to Preview'}
                        subContent={readonly ? '' : 'Click anywhere to start adding telecommand'}
                        onClick={() => {
                            if (!readonly) onAddCommand?.(selectedTask!, null);
                        }}
                    />
                )}

                {hasCommands && tab === 'telecommand' && (
                    <div className='schedule__table__container telecommand__table'>
                        <div className='schedule__table__container__header'>
                            {isValidateMode ? (
                                <div className='table__header__cell__container'>
                                    <span className='table__cell header__cell w-4' />
                                    <div className='table__cell header__cell w-35'>
                                        <span className='cell__detail'>TELECOMMAND</span>
                                    </div>
                                    <div className='table__cell header__cell w-25'>
                                        <span className='cell__detail'>COMPONENTS</span>
                                    </div>
                                    <div className='table__cell header__cell w-25'>
                                        <span className='cell__detail'>START TIME</span>
                                    </div>
                                    <div className='table__cell header__cell w-15'>
                                        <span className='cell__detail'>DURATION</span>
                                    </div>
                                </div>
                            ) : (
                                <div className='table__header__cell__container'>
                                    <span className='table__cell header__cell w-4' />
                                    <span className='table__cell header__cell w-4' />
                                    <div className='table__cell header__cell w-30'>
                                        <span className='cell__detail'>TELECOMMAND</span>
                                    </div>
                                    <div className='table__cell header__cell w-25'>
                                        <span className='cell__detail'>SUBSYSTEM</span>
                                    </div>
                                    <div className='table__cell header__cell w-25'>
                                        <span className='cell__detail'>START TIME</span>
                                    </div>
                                    <div className='table__cell header__cell w-10'>
                                        <span className='cell__detail'>DURATION</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className='schedule__table__container__body'>
                            {isValidateMode ? (
                                (() => {
                                    const currentTaskEntries = selectedTask
                                        ? (taskTimelineMap[selectedTask.taskId] ||
                                            ((selectedTask as any)?.testCaseResultId && taskTimelineMap[(selectedTask as any).testCaseResultId]) ||
                                            (selectedTask.taskName && taskTimelineMap[selectedTask.taskName.toLowerCase()]) ||
                                            Object.values(taskTimelineMap)[0])
                                        : null;
                                    const selectedTaskStatusInfo = selectedTask ? getTaskStatusInfo(selectedTask) : null;

                                    return selectedTaskCommands.map((item, cmdIndex) => {
                                        const selected = item?.commandId === selectedTelecommand?.commandId;
                                        const recentlyEdited = showRecentlyEditedCommand && item?.commandId === recentlyEditedCommandId;
                                        const view = readonly ? 'readonly' : 'editable';
                                        const componentName = item?.component || item?.subsystem || '--';
                                        const startTimeStr = item?.epochISO8601 || item?.epochISO8601InMs || (item?.epoch ? dayjs.unix(item.epoch).utc().toISOString() : '--');
                                        const durationStr = `${item?.duration ?? 0} secs`;

                                        const matchingEntry = currentTaskEntries
                                            ? findMatchingEntry(item, cmdIndex, currentTaskEntries)
                                            : null;

                                        let commandStatus = 'SCHEDULED';
                                        if (executedSchedule || selectedSchedule?.status === 'COMPLETED' || selectedTaskStatusInfo?.title === 'Completed') {
                                            commandStatus = 'COMPLETED';
                                        } else if (matchingEntry?.status) {
                                            commandStatus = String(matchingEntry.status).toUpperCase();
                                        } else if (item?.status) {
                                            commandStatus = String(item.status).toUpperCase();
                                        }

                                        let commandIcon = <TeleCommandsIcon />;
                                        if (executedSchedule || selectedSchedule?.status === 'COMPLETED' || commandStatus === 'COMPLETED' || commandStatus === 'SUCCESS' || commandStatus === 'PASSED') {
                                            commandIcon = <TaskCompleteSvg />;
                                        } else if (commandStatus === 'RUNNING' || commandStatus === 'IN_PROGRESS') {
                                            commandIcon = <SpinLoaderSvg className="rotating" />;
                                        }

                                        return (
                                            <div
                                                key={item?.commandId || `cmd-${cmdIndex}`}
                                                className={`table__body__cell__container 
                                                    ${selected ? 'active' : ''}
                                                    ${recentlyEdited ? 'recently__edited__row' : ''}
                                                `}
                                                ref={recentlyEdited ? recentlyEditedRowRef : undefined}
                                                onClick={() => {
                                                    onCommandSelect(view, item);
                                                }}
                                            >
                                                <div className='table__cell w-4'>
                                                    <div className='cell__icon'>
                                                        <CustomToolTip title={commandStatus} placement='top'>
                                                            {commandIcon}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-35'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={item?.name} placement='top'>
                                                            {item?.name}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-25'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={componentName} placement='top'>
                                                            {componentName}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-25'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={startTimeStr} placement='top'>
                                                            {startTimeStr}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                                <div className='table__cell w-15'>
                                                    <div className='cell__detail'>
                                                        <CustomToolTip title={durationStr} placement='top'>
                                                            {durationStr}
                                                        </CustomToolTip>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    });
                                })()
                            ) : (
                                selectedTaskCommands.map((item) => {
                                    const selected = item?.commandId === selectedTelecommand?.commandId;
                                    const recentlyEdited = showRecentlyEditedCommand && item?.commandId === recentlyEditedCommandId;
                                    const view = readonly ? 'readonly' : 'editable';
                                    const showButton = showAddButton === 'add-telecommand' && selected;
                                    const details = getDetails(item?.epoch, item?.epoch + item?.duration);
                                    return (
                                        <div
                                            key={item?.commandId}
                                            className={`table__body__cell__container 
                                                ${selected ? 'active' : ''}
                                                ${recentlyEdited ? 'recently__edited__row' : ''}
                                                ${showButton ? 'show__button' : ''}
                                            `}
                                            ref={recentlyEdited ? recentlyEditedRowRef : undefined}
                                            onClick={() => {
                                                onCommandSelect(view, item);
                                            }}
                                        >
                                            {!readonly && (
                                                <NewAddButton
                                                    title='Add New Telecommand'
                                                    onClick={() => {
                                                        onAddCommand?.(selectedTask!, item);
                                                    }}
                                                />
                                            )}
                                            <div className='table__cell w-4'>
                                                <div className='cell__icon'>
                                                    {(ongoingSchedule || executedSchedule) && (
                                                        <CustomToolTip title={details?.status || ''} placement='top'>
                                                            {details?.icon}
                                                        </CustomToolTip>
                                                    )}
                                                </div>
                                            </div>
                                            <div className='table__cell w-4'>
                                                <div className='cell__icon'>
                                                    <CustomToolTip title={item?.type} placement='top'>
                                                        <TeleCommandsIcon />
                                                    </CustomToolTip>
                                                </div>
                                            </div>
                                            <div className='table__cell w-30'>
                                                <div className='cell__detail'>
                                                    <CustomToolTip title={item?.name} placement='top'>
                                                        {item?.name}
                                                    </CustomToolTip>
                                                </div>
                                            </div>
                                            <div className='table__cell w-25'>
                                                <div className='cell__detail'>
                                                    <CustomToolTip title={item?.subsystem} placement='top'>
                                                        {item?.subsystem}
                                                    </CustomToolTip>
                                                </div>
                                            </div>
                                            <div className='table__cell w-25'>
                                                <div className='cell__detail'>
                                                    <CustomToolTip title={item?.epochISO8601} placement='top'>
                                                        {item?.epochISO8601}
                                                    </CustomToolTip>
                                                </div>
                                            </div>
                                            <div className='table__cell w-10'>
                                                <div className='cell__detail'>
                                                    <CustomToolTip title={`${item?.duration} sec`} placement='top'>
                                                        {item?.duration ?? 0} sec
                                                    </CustomToolTip>
                                                </div>
                                            </div>
                                            {!hideGroupButtons && (
                                                <div className='table__cell menu__container'>
                                                    <GroupButtonList
                                                        placement='left'
                                                        id={item?.commandId as string}
                                                        open={openMenu}
                                                        toggle={(value) => {
                                                            setOpenMenu(value);
                                                        }}
                                                        buttons={MenuList}
                                                        onClick={(btn) => {
                                                            onMenuClick(btn, view, 'telecommand', {
                                                                name: item?.name,
                                                                scheduleId: scheduleId as string,
                                                                taskId: selectedTask?.taskId as string,
                                                                commandId: item?.commandId as string,
                                                            });
                                                        }}
                                                    >
                                                        <i className='fe fe-more-vertical opacity-75' />
                                                    </GroupButtonList>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {!isValidateMode && hasCommands && tab === 'raw-data' && (
                    <div className='schedule__table__container pb-2'>
                        <div className='schedule__table__container__body gap-3'>
                            {selectedTaskCommands.map((item) => (
                                <div key={item?.commandId} className='row__data__container'>
                                    <div className='row__details__icon__container'>
                                        <TaskUpcommingSvg />
                                    </div>
                                    <div className='row__data__details__container'>
                                        <span className='row__data__details__text'>
                                            TC/TM COMMAND | {item?.subsystem}
                                        </span>
                                        <span className='row__data__details__text'>
                                            {base64ToHex(item?.sequenceDetails?.sequenceBase64)}
                                        </span>
                                    </div>
                                    <div className='row__data__badge'>
                                        <span className='row__detail__time'>{item?.epochISO8601}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScheduleTaskView;
